import { useEffect, useMemo, useState } from 'react';
import { demeterNewsApi, marketIntelligenceApi } from '../../../../Apis/Apis';
import applicationSettings from '../../../../Core/Settings/ApplicationSettings';
import { MarketIntelligenceSubscriptionCountry } from '../../../../Generated/Raven';
import { Application, DemeterMarket, DemeterMarketNewsType, DemeterUserStoreType } from '../../../../Generated/Raven-Demeter';
import { useApplicationSelector } from '../../../../Redux/ReduxStore';
import { selectUserMarkets } from '../../../../Redux/Slices/UserSlice';
import useApi from '../../../Apis/Hooks/useApiHook';
import useApiWithoutAutoExecute from '../../../Apis/Hooks/useApiWithoutAutoExecute';
import useUserStoreApi from '../../../Apis/Hooks/useUserStoreApiHook';
import useLanguage from '../../../Services/Language/useLanguageHook';
import PriceSummarySmall from '../../Dashboard/PriceSummarySmall';
import CustomDashboardMarketIntelligenceReportContainer from './CustomDashboardMarketIntelligenceReportContainer';
import styles from './CustomDefaultDashboardComponents.module.scss';

interface IListArticlesRequest {
    searchTerm: string | undefined;
    author: string | undefined;
    isExactSearch: boolean | undefined;
    dateRangeStart: string | undefined;
    dateRangeEnd: string | undefined;
    marketId: number;
    contentConfigurationId: string | undefined;
    languageCode: string | undefined;
    usePremiumTrial: boolean;
    mobileApplication: Application | undefined;
    subscriptionCountry: MarketIntelligenceSubscriptionCountry | undefined;
    source: string;
    showOnlySubscibedArticles: boolean;
    take: number;
    skip: number;
}

interface IListMarketNewsRequest {
    MarketNewsType: DemeterMarketNewsType;
    searchTerm: string | undefined;
    languageCode: string | undefined;
    take: number;
}
const maximumNumberOfArticlesToStore = 200;
const refreshTime = 300000; // Refreshes every 5 minutes.
const CustomDashboardMarketIntelligenceReports: React.FC = () => {
    const [translations] = useLanguage();
    const markets = useApplicationSelector(selectUserMarkets);
    const [userStoreValue, updateUserStore] = useUserStoreApi(DemeterUserStoreType.MarketIntelligence);

    const [energyArticlesRequest, setEnergyArticlesRequest] = useState<IListArticlesRequest>({
        searchTerm: undefined,
        author: undefined,
        isExactSearch: undefined,
        dateRangeStart: undefined,
        dateRangeEnd: undefined,
        marketId: 0,
        contentConfigurationId: undefined,
        languageCode: undefined,
        usePremiumTrial: false,
        mobileApplication: undefined,
        subscriptionCountry: undefined,
        source: applicationSettings.applicationName,
        showOnlySubscibedArticles: true,
        take: 20,
        skip: 0,
    });

    const [dairyHeadlineNewsRequest, setDairyHeadlineNewsRequest] = useState<IListMarketNewsRequest>({
        MarketNewsType: DemeterMarketNewsType.Headline,
        searchTerm: undefined,
        languageCode: undefined,
        take: 20,
    });

    const [dairyReportsRequest, setDairyReportsRequest] = useState<IListMarketNewsRequest>({
        MarketNewsType: DemeterMarketNewsType.Reports,
        searchTerm: undefined,
        languageCode: undefined,
        take: 20,
    });

    const [, , listMarketsResponse] = useApi(() => marketIntelligenceApi.listMarkets());

    const [isLoadingDemeterDairyReports, refreshDairyReportsApi, demeterDairyReportsResponse] = useApi(() => {
        if (!dairyReportsRequest) {
            return null;
        }

        return demeterNewsApi.listDemeterHeadlineNews(
            dairyReportsRequest.MarketNewsType,
            dairyReportsRequest.searchTerm,
            dairyReportsRequest.languageCode,
            dairyReportsRequest.take,
        );
    });

    const [isLoadingDemeterHeadlineNews, refreshHeadlineNewsApi, demeterHeadlineNewsResponse] = useApi(() => {
        if (!dairyHeadlineNewsRequest) {
            return null;
        }

        return demeterNewsApi.listDemeterHeadlineNews(
            dairyHeadlineNewsRequest.MarketNewsType,
            dairyHeadlineNewsRequest.searchTerm,
            dairyHeadlineNewsRequest.languageCode,
            dairyHeadlineNewsRequest.take,
        );
    });

    const [isLoadingEnergyArticles, refreshEnergyArticles, listEnergyArticlesResponse] = useApiWithoutAutoExecute(() => {
        if (!energyArticlesRequest) {
            return null;
        }

        return marketIntelligenceApi.listArticles(
            energyArticlesRequest.searchTerm,
            energyArticlesRequest.author,
            energyArticlesRequest.isExactSearch,
            energyArticlesRequest.dateRangeStart,
            energyArticlesRequest.dateRangeEnd,
            energyArticlesRequest.marketId,
            energyArticlesRequest.contentConfigurationId,
            energyArticlesRequest.languageCode,
            energyArticlesRequest.usePremiumTrial,
            energyArticlesRequest.mobileApplication,
            energyArticlesRequest.subscriptionCountry,
            energyArticlesRequest.source,
            energyArticlesRequest.showOnlySubscibedArticles,
            energyArticlesRequest.take,
            energyArticlesRequest.skip,
        );
    });

    const hasDairy = useMemo(() => markets.some((x) => x === DemeterMarket.Dairy), [markets]);
    const hasEnergy = useMemo(() => markets.some((x) => x === DemeterMarket.Energy), [markets]);
    const clickedArticleIds = useMemo(() => {
        if (!userStoreValue?.userStore?.value?.clickArticleLinkIds) {
            return [];
        }

        return userStoreValue.userStore.value.clickArticleLinkIds;
    }, [userStoreValue]);

    const handleLinkClick = (linkId: string) => {
        const visitedArticleIds: string[] = [...(userStoreValue?.userStore?.value?.clickArticleLinkIds || [])].filter((x) => x !== linkId);
        visitedArticleIds.push(linkId);

        if (visitedArticleIds.length >= maximumNumberOfArticlesToStore) {
            const removedOldIds = visitedArticleIds.splice(visitedArticleIds.length - maximumNumberOfArticlesToStore);
            updateUserStore({ clickArticleLinkIds: removedOldIds });
            return;
        }

        updateUserStore({ clickArticleLinkIds: visitedArticleIds });
    };

    useEffect(() => {
        if (!energyArticlesRequest.marketId) {
            return;
        }

        refreshEnergyArticles();
    }, [energyArticlesRequest.marketId]);

    useEffect(() => {
        if (!listMarketsResponse) {
            return;
        }

        const energyMatch = listMarketsResponse.rows?.find((x) => x.name === 'Energy');

        if (!energyMatch || !energyMatch.id || !hasEnergy) {
            return;
        }

        setEnergyArticlesRequest((previous) => ({ ...previous, marketId: energyMatch.id }));
    }, [listMarketsResponse, hasEnergy]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (hasEnergy) {
                const energyMatch = listMarketsResponse?.rows?.find((x) => x.name === 'Energy');
                if (energyMatch) {
                    refreshEnergyArticles({ ...energyArticlesRequest, marketId: energyMatch.id });
                }
            }

            if (hasDairy) {
                refreshHeadlineNewsApi();
                refreshDairyReportsApi();
            }
        }, refreshTime);

        return () => clearInterval(interval);
    }, [hasEnergy, hasDairy, listMarketsResponse, refreshEnergyArticles, refreshHeadlineNewsApi, refreshDairyReportsApi]);

    return (
        <div className={styles.market_intelligence_container}>
            {hasEnergy && (
                <CustomDashboardMarketIntelligenceReportContainer
                    title={translations.market[DemeterMarket.Energy]}
                    isLoading={isLoadingEnergyArticles}
                    newsOrArticleResponse={listEnergyArticlesResponse}
                    clickedArticleIds={clickedArticleIds}
                    onSearchTermChange={(value) => setEnergyArticlesRequest((previous) => ({ ...previous, searchTerm: value }))}
                    onLinkClick={handleLinkClick}
                    onSearch={() => refreshEnergyArticles()}
                    enableModal={false}
                    track={{ eventCategory: 'Dashboard', eventAction: 'MarketIntelligenceReportsEnergy' }}
                />
            )}
            {hasDairy && (
                <CustomDashboardMarketIntelligenceReportContainer
                    title={`${translations.market[DemeterMarket.Dairy]} - ${translations.dashboard.latestNews.latest}`}
                    isLoading={isLoadingDemeterHeadlineNews}
                    newsOrArticleResponse={demeterHeadlineNewsResponse}
                    clickedArticleIds={clickedArticleIds}
                    onSearchTermChange={(value) => setDairyHeadlineNewsRequest((previous) => ({ ...previous, searchTerm: value }))}
                    onLinkClick={handleLinkClick}
                    onSearch={() => refreshHeadlineNewsApi()}
                    enableModal
                    track={{ eventCategory: 'Dashboard', eventAction: 'MarketIntelligenceReportsDairyLatestNews' }}
                />
            )}
            {hasDairy && (
                <CustomDashboardMarketIntelligenceReportContainer
                    title={translations.market[DemeterMarket.Dairy]}
                    isLoading={isLoadingDemeterDairyReports}
                    newsOrArticleResponse={demeterDairyReportsResponse}
                    clickedArticleIds={clickedArticleIds}
                    onSearchTermChange={(value) => setDairyReportsRequest((previous) => ({ ...previous, searchTerm: value }))}
                    onLinkClick={handleLinkClick}
                    onSearch={() => refreshDairyReportsApi()}
                    enableModal={false}
                    track={{ eventCategory: 'Dashboard', eventAction: 'MarketIntelligenceReportsDairy' }}
                />
            )}
            {hasDairy && !hasEnergy && (
                <div className={styles.market_intelligence_small_price_summaries}>
                    <PriceSummarySmall />
                </div>
            )}
        </div>
    );
};

export default CustomDashboardMarketIntelligenceReports;
